import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ArcDialog, ArcHeaderPeriodSelect, ArcPage, ArcPageHeader, ArcPanel, ArcSelect, ArcTableFrame, ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Button } from '../components/arc/button/button';
import { useFeather } from '../shell/useFeather';
import { useTabParam } from '../shell/useTabParam';
import { confirmDialog } from '../shell/dialogs';
import { ApiError } from '../api/client';
import {
  addUzaverkaCategory,
  addUzaverkaRule,
  checkUzaverkaEntry,
  deactivateUzaverkaRule,
  getUzaverka,
  getUzaverkaDetail,
  getUzaverkaNezarazeno,
  getUzaverkaRules,
  saveUzaverkaEntry,
  saveUzaverkaMonth,
  updateUzaverkaCategory,
  type LedgerCategory,
  type LedgerCell,
  type LedgerColumn,
  type LedgerColumnStatus,
  type LedgerInvoiceItem,
  type LedgerKind,
  type LedgerMonthStatus,
  type LedgerOverview,
  type LedgerRule,
} from '../api/uzaverka';
import './affilmaster-dashboard.css';
import './affilmaster-prijmy.css';
import './affilmaster-uzaverka.css';

type UzTab = 'prehled' | 'nezarazeno' | 'nastaveni';
const UZ_TABS: readonly UzTab[] = ['prehled', 'nezarazeno', 'nastaveni'];
const MONTH_CS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
const STATUS_LABEL: Record<LedgerColumnStatus, string> = { probiha: 'Probíhá', kontrola: 'Kontrola', uzavreno: 'Uzavřeno', budouci: 'Předpoklad' };
const MONTH_STATUSES: LedgerMonthStatus[] = ['probiha', 'kontrola', 'uzavreno'];

const fmtKc = (v: number) => `${Math.round(v).toLocaleString('cs-CZ')} Kč`;
const fmtAmount = (v: number, currency: string) => `${v.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency === 'EUR' ? '€' : 'Kč'}`;
const ymLabel = (ym: string) => { const [y, m] = ym.split('-'); return `${MONTH_CS[Number(m) - 1] ?? m} ${y ?? ''}`; };
const quarterOf = (period: string) => `${period.slice(0, 4)}-Q${Math.ceil(Number(period.slice(5, 7)) / 3)}`;
const errText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message || fallback : fallback);

function readParam(name: string): string {
  return new URLSearchParams(window.location.search).get(name) ?? '';
}
function writeParam(name: string, value: string | null) {
  const sp = new URLSearchParams(window.location.search);
  if (value) sp.set(name, value); else sp.delete(name);
  const q = sp.toString();
  window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : ''));
}

function descriptionRegexFor(description: string): string {
  const cut = description.split(' - ')[0] ?? description;
  const stripped = cut.replace(/\s*\d{2}\/\d{4}.*$/, '').replace(/\s+(leden|únor|březen|duben|květen|červen|červenec|srpen|září|říjen|listopad|prosinec).*$/i, '').trim();
  const escaped = (stripped || cut).slice(0, 60).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return `^${escaped}`;
}

export function AffilmasterUzaverkaPage() {
  const { me } = useAuth();
  const canManage = !!me?.permissionKeys?.includes('affilmaster:manage');
  const [tab, setTab] = useTabParam<UzTab>('tab', UZ_TABS, 'prehled');
  const [year, setYear] = useState(() => Number(readParam('year')) || new Date().getFullYear());
  const [data, setData] = useState<LedgerOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => { writeParam('year', String(year)); }, [year]);
  useEffect(() => {
    let cancelled = false;
    getUzaverka(year)
      .then((d) => { if (!cancelled) { setData(d); setErr(null); } })
      .catch((e: unknown) => { if (!cancelled) setErr(errText(e, 'Načtení uzávěrky selhalo')); });
    return () => { cancelled = true; };
  }, [year, reload]);
  useFeather([data, tab]);

  const refresh = useCallback(() => setReload((r) => r + 1), []);
  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const years = new Set([now - 1, now, now + 1, year]);
    return [...years].sort().map((y) => ({ value: String(y), label: String(y) }));
  }, [year]);

  return (
    <ArcPage className="affms-dash affms-prijmy am-uz">
      <ArcPageHeader
        eyebrow="AFFILMASTER"
        title="Uzávěrka"
        description="Měsíční výsledovka: příjmy z fakturace, výdaje a zisk k rozdělení"
        meta={<div className="am-uz-head-tools">
          {data?.invoicesThrough && <span className="am-uz-head-meta">Fakturace v UOL do {ymLabel(data.invoicesThrough)}</span>}
          <ArcHeaderPeriodSelect value={String(year)} onChange={(v) => setYear(Number(v))} options={yearOptions} />
        </div>}
      />
      {err && (
        <div className="am-uz-alert" role="alert">
          <i data-feather="alert-circle" />
          <div><strong>Uzávěrku se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}
      {!err && !data && <div className="am-uz-loading" role="status">Načítám uzávěrku…</div>}
      {data && (
        <div className="am-uz-stack">
          <div className="am-uz-tabs">
            <ArcTabs
              label="Uzávěrka"
              value={tab}
              onChange={setTab}
              items={[
                { value: 'prehled', label: 'Přehled' },
                { value: 'nezarazeno', label: data.unassigned > 0 ? `Nezařazené (${data.unassigned})` : 'Nezařazené' },
                { value: 'nastaveni', label: 'Nastavení' },
              ]}
            />
          </div>
          {tab === 'prehled' && <TabPrehled key={data.year} data={data} canManage={canManage} onChanged={refresh} />}
          {tab === 'nezarazeno' && <TabNezarazeno year={year} categories={data.categories} canManage={canManage} onChanged={refresh} />}
          {tab === 'nastaveni' && <TabNastaveni canManage={canManage} onChanged={refresh} />}
        </div>
      )}
    </ArcPage>
  );
}

function TabPrehled({ data, canManage, onChanged }: { data: LedgerOverview; canManage: boolean; onChanged: () => void }) {
  const [cell, setCell] = useState<string>(() => readParam('cell'));
  const [month, setMonth] = useState<string>(() => readParam('month'));
  const [quarter, setQuarter] = useState<string>(() => {
    if (data.columns.length <= 6) return 'all';
    const currentYear = Number(data.currentPeriod.slice(0, 4));
    const period = data.year > currentYear ? data.columns[0]?.period
      : data.year < currentYear ? data.columns[data.columns.length - 1]?.period
        : data.currentPeriod;
    return period ? quarterOf(period) : 'all';
  });
  useEffect(() => { writeParam('cell', cell || null); }, [cell]);
  useEffect(() => { writeParam('month', month || null); }, [month]);

  const quarterOptions = [...new Set(data.columns.map((col) => quarterOf(col.period)))].map((key) => {
    const months = data.columns.filter((col) => quarterOf(col.period) === key);
    const first = months[0] ? ymLabel(months[0].period) : '';
    const lastColumn = months[months.length - 1];
    const last = lastColumn ? ymLabel(lastColumn.period) : '';
    return { key, label: key.slice(-2), title: first === last ? first : `${first} – ${last}` };
  });
  const activeQuarter = quarter === 'all' || quarterOptions.some((option) => option.key === quarter) ? quarter : 'all';
  const visibleColumns = activeQuarter === 'all' ? data.columns : data.columns.filter((col) => quarterOf(col.period) === activeQuarter);
  const income = data.categories.filter((c) => c.kind === 'prijem');
  const expense = data.categories.filter((c) => c.kind === 'vydaj');
  const cellFor = (period: string, c: LedgerCategory): LedgerCell => data.cells[period]?.[String(c.id)] ?? { auto: null, manual: null, amount: 0, checked: false, note: null, items: 0 };
  const editable = (col: LedgerColumn) => !col.forecast && col.status !== 'uzavreno';

  const renderRows = (list: LedgerCategory[]) => list.map((c) => (
    <tr key={c.id} className="am-uz-row">
      <th scope="row" className="am-uz-cat">
        <span className="am-uz-cat-line">
          <span className="am-uz-cat-name" title={c.name}>{c.name}</span>
          {c.source === 'uol'
            ? <span className="am-uz-source" title={c.kind === 'prijem' ? 'Počítá se z vystavených faktur v UOL' : 'Počítá se z přijatých faktur v UOL'}>UOL</span>
            : c.owner && <em className="am-uz-owner" title="Kdo položku potvrzuje">{c.owner}</em>}
        </span>
      </th>
      {visibleColumns.map((col) => {
        const v = cellFor(col.period, c);
        const cls = ['am-uz-cell', col.forecast ? 'is-forecast' : '', v.manual != null ? 'is-manual' : '', v.checked ? 'is-checked' : '', v.amount === 0 ? 'is-zero' : ''].filter(Boolean).join(' ');
        return (
          <td key={col.period} className={cls}>
            <button type="button" className="am-uz-cell-btn" onClick={() => setCell(`${col.period}:${c.id}`)} aria-label={`${c.name}, ${ymLabel(col.period)}: ${fmtKc(v.amount)}${v.checked ? ', potvrzeno' : ''}`} title={col.forecast ? 'Předpoklad' : `${c.name}, ${ymLabel(col.period)}`}>
              <span>{v.amount ? fmtKc(v.amount) : '0 Kč'}</span>
              {v.checked && <i className="am-uz-check" aria-label="Potvrzeno" />}
            </button>
          </td>
        );
      })}
    </tr>
  ));

  const totalRow = (label: string, pick: (col: LedgerColumn) => number, cls = '') => (
    <tr className={`am-uz-total ${cls}`.trim()}>
      <th scope="row" className="am-uz-cat">{label}</th>
      {visibleColumns.map((col) => <td key={col.period} className={`am-uz-cell${col.forecast ? ' is-forecast' : ''}`}><span>{fmtKc(pick(col))}</span></td>)}
    </tr>
  );

  const selected = cell ? parseCell(cell, data) : null;
  const selectedMonth = month ? data.columns.find((c) => c.period === month) ?? null : null;

  return (
    <div className="tab-content am-uz-overview">
      {quarterOptions.length > 1 && (
        <div className="am-uz-quarterbar">
          <span className="am-uz-quarterbar-label">Měsíce</span>
          <div className="am-uz-quarter-options" role="group" aria-label="Zobrazené měsíce uzávěrky">
            <Button type="button" variant="secondary" size="sm" aria-pressed={activeQuarter === 'all'} onClick={() => setQuarter('all')}>Všechny měsíce</Button>
            {quarterOptions.map((option) => <Button key={option.key} type="button" variant="secondary" size="sm" aria-label={`${option.label.replace('Q', '')}. čtvrtletí ${option.key.slice(0, 4)}`} title={option.title} aria-pressed={activeQuarter === option.key} onClick={() => setQuarter(option.key)}>{option.label}</Button>)}
          </div>
        </div>
      )}
      <ArcTableFrame label="Uzávěrka po měsících" className="am-uz-frame" sticky="container">
        <table className="am-uz-table" style={{ minWidth: `${260 + visibleColumns.length * 136}px` }}>
          <thead>
            <tr>
              <th scope="col" className="am-uz-cat am-uz-cat-head">Položka</th>
              {visibleColumns.map((col) => (
                <th key={col.period} scope="col" className={`am-uz-month is-${col.status}`}>
                  <button type="button" className="am-uz-month-btn" onClick={() => setMonth(col.period)} disabled={col.forecast && !canManage}>
                    <strong>{MONTH_CS[Number(col.period.slice(5, 7)) - 1]} {col.period.slice(0, 4)}</strong>
                    <span className={`am-uz-status is-${col.status}`}>{STATUS_LABEL[col.status]}</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="am-uz-group"><th scope="rowgroup" colSpan={visibleColumns.length + 1}>Příjmy</th></tr>
            {renderRows(income)}
            {totalRow('Celkem příjmy', (c) => c.income, 'is-income')}
            <tr className="am-uz-group"><th scope="rowgroup" colSpan={visibleColumns.length + 1}>Výdaje</th></tr>
            {renderRows(expense)}
            {totalRow('Celkem výdaje', (c) => c.expense, 'is-expense')}
            <tr className="am-uz-group"><th scope="rowgroup" colSpan={visibleColumns.length + 1}>Výsledek</th></tr>
            {totalRow('Čistý zisk', (c) => c.profit, 'is-profit')}
            <tr className="am-uz-total is-reserve">
              <th scope="row" className="am-uz-cat">Rezerva</th>
              {visibleColumns.map((col) => <td key={col.period} className={`am-uz-cell${col.forecast ? ' is-forecast' : ''}`}><span><small className="am-uz-reserve-pct">{col.reservePct.toLocaleString('cs-CZ')} %</small>{fmtKc(col.reserve)}</span></td>)}
            </tr>
            {totalRow('Zisk k rozdělení', (c) => c.distributable, 'is-distributable')}
          </tbody>
        </table>
      </ArcTableFrame>
      <ArcPanel as="div" className="am-uz-explainer">
        <i data-feather="info" />
        <span>Položky označené UOL se sčítají z vystavených a přijatých faktur podle měsíce služby v názvu položky (jinak podle DUZP), € přepočteno kurzem 25 Kč. Ruční hodnota má přednost před výpočtem. Uzavřený měsíc se už nepřepočítává.</span>
      </ArcPanel>
      {selected && (
        <CellDialog
          period={selected.period}
          category={selected.category}
          cell={cellFor(selected.period, selected.category)}
          column={selected.column}
          editable={canManage && editable(selected.column)}
          canCheck={canManage && !selected.column.forecast}
          onClose={() => setCell('')}
          onChanged={onChanged}
        />
      )}
      {selectedMonth && (
        <MonthDialog column={selectedMonth} canManage={canManage} onClose={() => setMonth('')} onChanged={onChanged} />
      )}
    </div>
  );
}

function parseCell(cell: string, data: LedgerOverview): { period: string; category: LedgerCategory; column: LedgerColumn } | null {
  const [period, id] = cell.split(':');
  const category = data.categories.find((c) => String(c.id) === id);
  const column = data.columns.find((c) => c.period === period);
  return period && category && column ? { period, category, column } : null;
}

function CellDialog({ period, category, cell, column, editable, canCheck, onClose, onChanged }: {
  period: string; category: LedgerCategory; cell: LedgerCell; column: LedgerColumn; editable: boolean; canCheck: boolean; onClose: () => void; onChanged: () => void;
}) {
  const [items, setItems] = useState<LedgerInvoiceItem[] | null>(category.source === 'uol' ? null : []);
  const [manual, setManual] = useState(cell.manual == null ? '' : String(cell.manual));
  const [note, setNote] = useState(cell.note ?? '');
  const [checked, setChecked] = useState(cell.checked);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (category.source !== 'uol' || column.forecast) { setItems([]); return; }
    let cancelled = false;
    getUzaverkaDetail(period, category.id).then((d) => { if (!cancelled) setItems(d.items); }).catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [period, category.id, category.source, column.forecast]);
  useFeather([items]);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const amount = manual.trim() === '' ? null : Number(manual.replace(/\s/g, '').replace(',', '.'));
      if (amount != null && !Number.isFinite(amount)) { setErr('Zadej částku v Kč'); setBusy(false); return; }
      await saveUzaverkaEntry(period, category.id, amount, note.trim() || null);
      if (checked !== cell.checked) await checkUzaverkaEntry(period, category.id, checked);
      onChanged(); onClose();
    } catch (e) { setErr(errText(e, 'Uložení selhalo')); } finally { setBusy(false); }
  }
  async function toggleCheck() {
    setBusy(true); setErr(null);
    try { await checkUzaverkaEntry(period, category.id, !checked); setChecked(!checked); onChanged(); }
    catch (e) { setErr(errText(e, 'Potvrzení selhalo')); } finally { setBusy(false); }
  }

  const grouped = useMemo(() => {
    const map = new Map<string, { customer: string; total: number; rows: LedgerInvoiceItem[] }>();
    for (const it of items ?? []) {
      const g = map.get(it.contactId) ?? { customer: it.customerName || it.contactId, total: 0, rows: [] };
      g.total += it.amountCzk; g.rows.push(it); map.set(it.contactId, g);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [items]);

  return (
    <ArcDialog
      title={`${category.name}, ${ymLabel(period)}`}
      description={column.forecast ? 'Předpoklad, počítá se z výchozí částky nebo posledního měsíce.' : category.source === 'uol' ? `Součet položek ${category.kind === 'prijem' ? 'vystavených' : 'přijatých'} faktur v UOL za tento měsíc.` : 'Ručně zadávaná položka.'}
      onClose={onClose}
      size="large"
      className="p1-arc-scope am-uz-dialog"
      bodyClassName="am-uz-dialog-body"
      actions={<>
        {canCheck && category.source !== 'uol' && <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void toggleCheck()}>{checked ? 'Zrušit potvrzení' : `Potvrdit${category.owner ? ` (${category.owner})` : ''}`}</Button>}
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Zavřít</Button>
        {editable && <Button type="button" size="sm" loading={busy} onClick={() => void save()}>Uložit</Button>}
      </>}
    >
      {err && <div className="am-uz-alert" role="alert">{err}</div>}
      <div className="am-uz-dialog-grid">
        <div className="am-uz-dialog-metric"><span>V uzávěrce</span><strong>{fmtKc(cell.amount)}</strong><small>{cell.manual != null ? 'ruční hodnota' : category.source === 'uol' ? 'z fakturace' : 'bez hodnoty'}</small></div>
        {category.source === 'uol' && <div className="am-uz-dialog-metric"><span>Z fakturace</span><strong>{fmtKc(cell.auto ?? 0)}</strong><small>{cell.items} {cell.items === 1 ? 'položka' : cell.items >= 2 && cell.items <= 4 ? 'položky' : 'položek'}</small></div>}
        {category.source === 'uol'
          ? <div className="am-uz-dialog-metric"><span>Zdroj</span><strong>UOL</strong><small>{STATUS_LABEL[column.status]}</small></div>
          : <div className="am-uz-dialog-metric"><span>Stav</span><strong>{checked ? 'Potvrzeno' : 'Nepotvrzeno'}</strong><small>{STATUS_LABEL[column.status]}</small></div>}
      </div>
      {editable && (
        <div className="am-uz-dialog-form">
          <label><span>{category.source === 'uol' ? 'Ruční hodnota (přepíše výpočet)' : 'Částka v Kč'}</span><input className="form-control form-control-sm" inputMode="decimal" value={manual} onChange={(e) => setManual(e.target.value)} placeholder={category.source === 'uol' ? 'nechat prázdné = z fakturace' : '0'} /></label>
          <label><span>Poznámka</span><input className="form-control form-control-sm" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} /></label>
        </div>
      )}
      {!editable && cell.note && <p className="am-uz-dialog-note">{cell.note}</p>}
      {category.source === 'uol' && !column.forecast && (
        <div className="am-uz-items">
          {items == null && <div className="text-muted small">Načítám položky…</div>}
          {items && items.length === 0 && <div className="text-muted small">Žádné položky faktur v tomto měsíci.</div>}
          {grouped.map((g) => (
            <div key={g.customer} className="am-uz-items-group">
              <div className="am-uz-items-head"><strong>{g.customer}</strong><span>{fmtKc(g.total)}</span></div>
              {g.rows.map((it, i) => (
                <div key={`${it.invoiceNumber}-${i}`} className="am-uz-item">
                  <span className="am-uz-item-desc">{it.description}</span>
                  <span className="am-uz-item-inv">{it.invoiceNumber}</span>
                  <span className="am-uz-item-amt">{it.currency === 'EUR' ? `${fmtAmount(it.amountOriginal, 'EUR')} = ` : ''}{fmtKc(it.amountCzk)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </ArcDialog>
  );
}

function MonthDialog({ column, canManage, onClose, onChanged }: { column: LedgerColumn; canManage: boolean; onClose: () => void; onChanged: () => void }) {
  const [status, setStatus] = useState<LedgerMonthStatus>(column.status === 'budouci' ? 'probiha' : column.status);
  const [reserve, setReserve] = useState(String(column.reservePct));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const closing = status === 'uzavreno' && column.status !== 'uzavreno';
  async function save() {
    setBusy(true); setErr(null);
    try {
      const pct = Number(reserve.replace(',', '.'));
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) { setErr('Rezerva musí být 0 až 100 %'); setBusy(false); return; }
      await saveUzaverkaMonth(column.period, { status, reservePct: pct });
      onChanged(); onClose();
    } catch (e) { setErr(errText(e, 'Uložení selhalo')); } finally { setBusy(false); }
  }
  return (
    <ArcDialog
      title={ymLabel(column.period)}
      description={column.forecast ? 'Měsíc ještě nezačal, hodnoty jsou předpoklad.' : `Příjmy ${fmtKc(column.income)}, výdaje ${fmtKc(column.expense)}, zisk ${fmtKc(column.profit)}.`}
      onClose={onClose}
      size="medium"
      className="p1-arc-scope am-uz-dialog"
      bodyClassName="am-uz-dialog-body"
      actions={<>
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Zavřít</Button>
        {canManage && !column.forecast && <Button type="button" size="sm" loading={busy} onClick={() => void save()}>{closing ? 'Uzavřít měsíc' : 'Uložit'}</Button>}
      </>}
    >
      {err && <div className="am-uz-alert" role="alert">{err}</div>}
      <div className="am-uz-dialog-grid">
        <div className="am-uz-dialog-metric"><span>Čistý zisk</span><strong>{fmtKc(column.profit)}</strong><small>příjmy minus výdaje</small></div>
        <div className="am-uz-dialog-metric"><span>Rezerva</span><strong>{fmtKc(column.reserve)}</strong><small>{column.reservePct.toLocaleString('cs-CZ')} % ze zisku</small></div>
        <div className="am-uz-dialog-metric"><span>K rozdělení</span><strong>{fmtKc(column.distributable)}</strong><small>{STATUS_LABEL[column.status]}</small></div>
      </div>
      {canManage && !column.forecast && (
        <div className="am-uz-dialog-form">
          <ArcSelect label="Stav měsíce" value={status} onChange={(value) => setStatus(value as LedgerMonthStatus)} options={MONTH_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))} className="am-uz-select" />
          <label><span>Rezerva v %</span><input className="form-control form-control-sm" inputMode="decimal" value={reserve} onChange={(e) => setReserve(e.target.value)} /></label>
        </div>
      )}
      {closing && <p className="am-uz-dialog-note">Hodnoty z fakturace se zafixují a měsíc se přestane přepočítávat. Jde znovu otevřít.</p>}
      {column.closedAt && <p className="am-uz-dialog-note">Uzavřeno {new Date(column.closedAt).toLocaleString('cs-CZ')}</p>}
    </ArcDialog>
  );
}

function TabNezarazeno({ year, categories, canManage, onChanged }: { year: number; categories: LedgerCategory[]; canManage: boolean; onChanged: () => void }) {
  const [items, setItems] = useState<LedgerInvoiceItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    getUzaverkaNezarazeno(year).then((d) => { if (!cancelled) { setItems(d.items); setErr(null); } }).catch((e: unknown) => { if (!cancelled) setErr(errText(e, 'Načtení selhalo')); });
    return () => { cancelled = true; };
  }, [year, reload]);
  useFeather([items]);

  async function assign(it: LedgerInvoiceItem) {
    const key = `${it.invoiceNumber}|${it.description}`;
    const value = choice[key];
    if (value === undefined || value === '') return;
    setBusy(key);
    try {
      await addUzaverkaRule({
        categoryId: value === 'exclude' ? null : Number(value),
        uolContactId: it.contactId || null,
        productId: it.productId,
        descriptionRegex: descriptionRegexFor(it.description),
        note: `Zařazeno z faktury ${it.invoiceNumber}`,
      });
      setReload((r) => r + 1); onChanged();
    } catch (e) { setErr(errText(e, 'Zařazení selhalo')); } finally { setBusy(null); }
  }

  const options = [
    { value: '', label: 'Vyber kategorii' },
    ...categories.filter((c) => c.kind === 'prijem').map((c) => ({ value: String(c.id), label: c.name })),
    { value: 'exclude', label: 'Mimo uzávěrku (nepočítat)' },
  ];

  return (
    <div className="tab-content">
      {err && <div className="am-uz-alert" role="alert">{err}</div>}
      {items == null && !err && <div className="am-uz-loading" role="status">Načítám nezařazené položky…</div>}
      {items && items.length === 0 && <div className="am-uz-empty">Všechny položky faktur za rok {year} jsou zařazené.</div>}
      {items && items.length > 0 && (
        <ArcTableFrame label="Nezařazené položky faktur" className="am-uz-frame">
          <table className="am-uz-list">
            <thead><tr><th>Měsíc</th><th>Faktura</th><th>Zákazník</th><th>Produkt</th><th>Položka</th><th className="text-end">Částka</th>{canManage && <th>Zařadit</th>}</tr></thead>
            <tbody>
              {items.map((it, i) => {
                const key = `${it.invoiceNumber}|${it.description}`;
                return (
                  <tr key={`${key}-${i}`}>
                    <td>{ymLabel(it.period)}</td>
                    <td>{it.invoiceNumber}</td>
                    <td>{it.customerName || it.contactId}</td>
                    <td className="text-muted">{it.productId ?? '-'}</td>
                    <td>{it.description}</td>
                    <td className="text-end">{it.currency === 'EUR' ? `${fmtAmount(it.amountOriginal, 'EUR')} = ` : ''}{fmtKc(it.amountCzk)}</td>
                    {canManage && (
                      <td className="am-uz-assign">
                        <ArcSelect label={`Kategorie pro ${it.invoiceNumber}`} value={choice[key] ?? ''} onChange={(value) => setChoice({ ...choice, [key]: value })} options={options} className="am-uz-select" disabled={busy === key} />
                        <Button type="button" size="sm" disabled={busy === key || !choice[key]} onClick={() => void assign(it)}>Zařadit</Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ArcTableFrame>
      )}
      <div className="am-prijmy-note">
        <i data-feather="info" />
        <span>Zařazením vznikne pravidlo podle zákazníka, produktu a názvu položky. Stejné položky v dalších měsících se už zařadí samy. Pravidla upravíš v Nastavení.</span>
      </div>
    </div>
  );
}

function TabNastaveni({ canManage, onChanged }: { canManage: boolean; onChanged: () => void }) {
  const [rules, setRules] = useState<LedgerRule[] | null>(null);
  const [categories, setCategories] = useState<LedgerCategory[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [draft, setDraft] = useState<Record<number, { name: string; owner: string; defaultAmount: string }>>({});
  const [newCat, setNewCat] = useState<{ kind: LedgerKind; name: string; owner: string; defaultAmount: string }>({ kind: 'vydaj', name: '', owner: '', defaultAmount: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getUzaverkaRules().then((d) => {
      if (cancelled) return;
      setRules(d.rules); setCategories(d.categories);
      const next: Record<number, { name: string; owner: string; defaultAmount: string }> = {};
      for (const c of d.categories) next[c.id] = { name: c.name, owner: c.owner ?? '', defaultAmount: c.defaultAmount == null ? '' : String(c.defaultAmount) };
      setDraft(next);
    }).catch((e: unknown) => { if (!cancelled) setErr(errText(e, 'Načtení nastavení selhalo')); });
    return () => { cancelled = true; };
  }, [reload]);
  useFeather([rules, categories]);

  const catName = (id: number | null) => (id == null ? 'Mimo uzávěrku' : categories.find((c) => c.id === id)?.name ?? `#${id}`);

  async function saveCategory(c: LedgerCategory) {
    const d = draft[c.id]; if (!d) return;
    setBusy(true); setErr(null);
    try {
      await updateUzaverkaCategory(c.id, { name: d.name.trim(), owner: d.owner.trim() || null, defaultAmount: d.defaultAmount.trim() === '' ? null : Number(d.defaultAmount.replace(/\s/g, '').replace(',', '.')) });
      setReload((r) => r + 1); onChanged();
    } catch (e) { setErr(errText(e, 'Uložení selhalo')); } finally { setBusy(false); }
  }
  async function toggleActive(c: LedgerCategory) {
    setBusy(true); setErr(null);
    try { await updateUzaverkaCategory(c.id, { active: !c.active }); setReload((r) => r + 1); onChanged(); }
    catch (e) { setErr(errText(e, 'Změna selhala')); } finally { setBusy(false); }
  }
  async function createCategory() {
    if (newCat.name.trim().length < 2) { setErr('Název kategorie musí mít aspoň 2 znaky'); return; }
    setBusy(true); setErr(null);
    try {
      await addUzaverkaCategory({ kind: newCat.kind, name: newCat.name.trim(), owner: newCat.owner.trim() || null, defaultAmount: newCat.defaultAmount.trim() === '' ? null : Number(newCat.defaultAmount.replace(/\s/g, '').replace(',', '.')) });
      setNewCat({ kind: newCat.kind, name: '', owner: '', defaultAmount: '' }); setReload((r) => r + 1); onChanged();
    } catch (e) { setErr(errText(e, 'Založení selhalo')); } finally { setBusy(false); }
  }
  async function removeRule(r: LedgerRule) {
    const ok = await confirmDialog({ title: 'Zrušit pravidlo?', message: `${r.note ?? r.descriptionRegex ?? r.uolContactId ?? ''}. Položky, které sedí jen na tohle pravidlo, se vrátí mezi nezařazené.`, confirmLabel: 'Zrušit pravidlo' });
    if (!ok) return;
    setBusy(true); setErr(null);
    try { await deactivateUzaverkaRule(r.id); setReload((x) => x + 1); onChanged(); }
    catch (e) { setErr(errText(e, 'Zrušení selhalo')); } finally { setBusy(false); }
  }

  const renderCats = (kind: LedgerKind) => categories.filter((c) => c.kind === kind).map((c) => {
    const d = draft[c.id] ?? { name: c.name, owner: c.owner ?? '', defaultAmount: '' };
    return (
      <tr key={c.id} className={c.active ? '' : 'is-inactive'}>
        <td>{canManage ? <input className="form-control form-control-sm" aria-label={`Název: ${c.name}`} value={d.name} onChange={(e) => setDraft({ ...draft, [c.id]: { ...d, name: e.target.value } })} /> : c.name}</td>
        <td className="am-uz-col-owner">{c.source === 'uol' ? '-' : canManage ? <input className="form-control form-control-sm" aria-label={`Kdo potvrzuje: ${c.name}`} value={d.owner} maxLength={8} onChange={(e) => setDraft({ ...draft, [c.id]: { ...d, owner: e.target.value } })} /> : (c.owner ?? '-')}</td>
        <td className="text-muted">{c.source === 'uol' ? 'z fakturace' : 'ručně'}</td>
        <td className="am-uz-col-amount">{canManage ? <input className="form-control form-control-sm" aria-label={`Výchozí částka: ${c.name}`} inputMode="decimal" value={d.defaultAmount} onChange={(e) => setDraft({ ...draft, [c.id]: { ...d, defaultAmount: e.target.value } })} placeholder="-" /> : (c.defaultAmount == null ? '-' : fmtKc(c.defaultAmount))}</td>
        {canManage && (
          <td className="am-uz-assign">
            <Button type="button" size="sm" disabled={busy} onClick={() => void saveCategory(c)}>Uložit</Button>
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void toggleActive(c)}>{c.active ? 'Skrýt' : 'Obnovit'}</Button>
          </td>
        )}
      </tr>
    );
  });

  return (
    <div className="tab-content am-uz-settings">
      {err && <div className="am-uz-alert" role="alert">{err}</div>}
      {rules == null && !err && <div className="am-uz-loading" role="status">Načítám nastavení…</div>}
      {rules && (
        <>
          <section className="am-prijmy-section">
            <div className="am-prijmy-section-head"><div><span>KATEGORIE</span><h2>Položky uzávěrky</h2></div><p>Písmeno = kdo položku potvrzuje. Výchozí částka plní sloupec předpoklad.</p></div>
            <ArcTableFrame label="Kategorie" className="am-uz-frame">
              <table className="am-uz-list">
                <thead><tr><th>Název</th><th>Kdo</th><th>Zdroj</th><th>Výchozí částka</th>{canManage && <th />}</tr></thead>
                <tbody>
                  <tr className="am-uz-group"><th colSpan={canManage ? 5 : 4}>Příjmy</th></tr>
                  {renderCats('prijem')}
                  <tr className="am-uz-group"><th colSpan={canManage ? 5 : 4}>Výdaje</th></tr>
                  {renderCats('vydaj')}
                </tbody>
              </table>
            </ArcTableFrame>
            {canManage && (
              <div className="am-uz-newcat">
                <ArcSelect label="Typ položky" value={newCat.kind} onChange={(value) => setNewCat({ ...newCat, kind: value as LedgerKind })} options={[{ value: 'prijem', label: 'Příjem' }, { value: 'vydaj', label: 'Výdaj' }]} className="am-uz-select" />
                <input className="form-control form-control-sm" aria-label="Název nové položky" placeholder="Název nové položky" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} />
                <input className="form-control form-control-sm am-uz-col-owner" aria-label="Kdo potvrzuje novou položku" placeholder="Kdo" maxLength={8} value={newCat.owner} onChange={(e) => setNewCat({ ...newCat, owner: e.target.value })} />
                <input className="form-control form-control-sm am-uz-col-amount" aria-label="Výchozí částka nové položky" placeholder="Výchozí Kč" inputMode="decimal" value={newCat.defaultAmount} onChange={(e) => setNewCat({ ...newCat, defaultAmount: e.target.value })} />
                <Button type="button" size="sm" disabled={busy} onClick={() => void createCategory()}>Přidat položku</Button>
              </div>
            )}
          </section>
          <section className="am-prijmy-section">
            <div className="am-prijmy-section-head"><div><span>PRAVIDLA</span><h2>Zařazování položek faktur</h2></div><p>První odpovídající pravidlo vyhrává. Zákazník, produkt a výraz v názvu položky.</p></div>
            <ArcTableFrame label="Pravidla" className="am-uz-frame">
              <table className="am-uz-list">
                <thead><tr><th>Kategorie</th><th>Zákazník (UOL)</th><th>Produkt</th><th>Název položky</th><th>Poznámka</th>{canManage && <th />}</tr></thead>
                <tbody>
                  {rules.map((r) => (
                    <tr key={r.id}>
                      <td>{catName(r.categoryId)}{r.invoiceSource === 'am_purchase' && <span className="am-uz-source am-uz-rule-source">přijaté</span>}</td>
                      <td className="am-uz-mono">{r.uolContactId ? r.uolContactId.split('|').join(', ') : 'kdokoli'}</td>
                      <td className="am-uz-mono">{r.productId ?? 'jakýkoli'}</td>
                      <td className="am-uz-mono">{r.descriptionRegex ?? 'jakýkoli'}</td>
                      <td className="text-muted">{r.note ?? ''}</td>
                      {canManage && <td className="am-uz-assign"><Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void removeRule(r)}>Zrušit</Button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ArcTableFrame>
          </section>
        </>
      )}
    </div>
  );
}
